/**
 * Délai de renouvellement AMO.
 *
 * L'assurance maladie ne prend en charge un nouvel équipement qu'une fois le
 * délai écoulé depuis le précédent. Le magasin applique **deux ans, adultes et
 * enfants sans distinction** : c'est la règle retenue par l'opticien, et elle
 * vit ici seule pour qu'un changement de barème ne se cherche pas dans six
 * écrans.
 *
 * Le calcul ne dit jamais qu'un remboursement est acquis — il dit à partir de
 * quelle date il redevient possible. La différence compte : promettre un
 * remboursement qui n'arrive pas se paie au comptoir.
 */

/** Deux ans, en mois. Adultes et enfants. */
export const DELAI_AMO_MOIS = 24;

/** En deçà, l'échéance est annoncée comme proche plutôt que lointaine. */
export const SEUIL_AMO_PROCHE_JOURS = 60;

/**
 * - `RENOUVELABLE` — le délai est écoulé, l'AMO peut reprendre l'équipement.
 * - `PROCHE` — l'échéance tombe dans moins de deux mois.
 * - `COUVERT` — l'équipement précédent est encore dans ses deux ans.
 */
export type EtatAmo = 'RENOUVELABLE' | 'PROCHE' | 'COUVERT';

export const ETAT_AMO_LABELS: Record<EtatAmo, string> = {
  RENOUVELABLE: 'Renouvelable',
  PROCHE: 'Bientôt renouvelable',
  COUVERT: 'Pas encore renouvelable',
};

export type StatutAmo = {
  /** Date retenue comme point de départ, `YYYY-MM-DD`. */
  depart: string;
  /** Premier jour où un nouvel équipement redevient remboursable. */
  echeance: string;
  /** Jours restants ; négatif quand l'échéance est passée. */
  jours: number;
  etat: EtatAmo;
};

const MOTIF_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PAR_JOUR = 86_400_000;

function deuxChiffres(n: number): string {
  return String(n).padStart(2, '0');
}

/** Nombre de jours du mois — `mois` de 1 à 12. */
function joursDuMois(annee: number, mois: number): number {
  return new Date(Date.UTC(annee, mois, 0)).getUTCDate();
}

function versEpoch(jour: string): number | null {
  if (!MOTIF_DATE.test(jour)) return null;
  const [a, m, j] = jour.split('-').map(Number);
  if (!a || !m || !j) return null;
  return Date.UTC(a, m - 1, j);
}

/**
 * Date d'échéance : le même jour, `DELAI_AMO_MOIS` plus tard.
 *
 * Le jour est ramené au dernier du mois quand il n'existe pas à l'arrivée —
 * un 29 février donne un 28 février, jamais un 1er mars. Décaler d'un jour
 * une échéance de deux ans n'aurait aucune importance pratique ; afficher une
 * date qui n'existe pas, si.
 */
export function echeanceAmo(depart: string): string {
  const jour = depart.slice(0, 10);
  if (!MOTIF_DATE.test(jour)) return jour;

  const [a, m, j] = jour.split('-').map(Number);
  if (!a || !m || !j) return jour;

  const total = m - 1 + DELAI_AMO_MOIS;
  const annee = a + Math.floor(total / 12);
  const mois = (total % 12) + 1;

  return `${annee}-${deuxChiffres(mois)}-${deuxChiffres(Math.min(j, joursDuMois(annee, mois)))}`;
}

/**
 * Situation d'un client vis-à-vis du délai, à une date donnée.
 *
 * Renvoie `null` sur une date absente ou illisible : sans point de départ il
 * n'y a pas d'échéance, et une échéance inventée serait pire que pas
 * d'échéance du tout.
 */
export function statutAmo(
  depart: string | null | undefined,
  aujourdhui: string,
): StatutAmo | null {
  if (!depart) return null;

  const jour = depart.slice(0, 10);
  const debut = versEpoch(jour);
  const maintenant = versEpoch(aujourdhui.slice(0, 10));
  if (debut === null || maintenant === null) return null;

  const echeance = echeanceAmo(jour);
  const cible = versEpoch(echeance);
  if (cible === null) return null;

  const jours = Math.round((cible - maintenant) / MS_PAR_JOUR);
  const etat: EtatAmo =
    jours <= 0 ? 'RENOUVELABLE' : jours <= SEUIL_AMO_PROCHE_JOURS ? 'PROCHE' : 'COUVERT';

  return { depart: jour, echeance, jours, etat };
}
